import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { $getNearestNodeFromDOMNode } from 'lexical';
import './myHeaders.css';
import { useSelector, useDispatch } from "react-redux";
import { useParams } from 'react-router-dom';
import { debounce } from "lodash";
import { padServices } from '../../api';
import { setEditorSummary, updateIsSaving } from '../../app/EditorSlice';
import AiTextButton from '../AiTextButton/AiTextButton';
import { emitLoadData } from '../../Socket';
import { saveToLocalStorage, isEffectivelyOffline } from '../../utils/helper';

// Outline row treatment per heading depth, as in the Pad Standalone mockup.
// h1 -> 0, h2 -> 1, h3 and deeper -> 2.
// One weight step lighter per level; Montserrat at 12–13px already reads bold at 600.
const HEADING_SPECS = [
  { tag: 'H1', size: '12.5px', weight: 600, color: '#1f2430', chipBg: '#dbe1f5', chipColor: '#3f57a0' },
  { tag: 'H2', size: '12px', weight: 500, color: '#33374a', chipBg: '#eceff9', chipColor: '#6b7fc0' },
  { tag: 'H3', size: '11.5px', weight: 500, color: '#6b7080', chipBg: '#f2f4fb', chipColor: '#8d9bcb' },
];

const PanelIcon = ({ direction }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7080" strokeWidth="1.7">
    <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
    <path d="M9.5 4.5v15" />
    {direction === 'left'
      ? <path d="M15.5 9.5l-2.2 2.5 2.2 2.5" />
      : <path d="M13.3 9.5l2.2 2.5-2.2 2.5" />}
  </svg>
);

const OutlineEmptyIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#b3b9c9" strokeWidth="1.6" strokeLinecap="round">
    <path d="M5 6h14" />
    <path d="M8 12h11" />
    <path d="M11 18h8" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#526bb1" strokeWidth="1.7">
    <path d="M12 4l1.6 4.4L18 10l-4.4 1.6L12 16l-1.6-4.4L6 10l4.4-1.6z" />
    <path d="M19 15l.8 1.9L21.7 17.7l-1.9.8L19 20.4l-.8-1.9-1.9-.8 1.9-.8z" />
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3d4250" strokeWidth="1.9">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const Headers = ({ isMobile, isOpenOutline: isOpenOutlineProp, setIsOpenOutline: setIsOpenOutlineProp }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isOpenOutlineInternal, setIsOpenOutlineInternal] = useState(true);
  const isOpenOutline = typeof isOpenOutlineProp !== 'undefined' ? isOpenOutlineProp : isOpenOutlineInternal;
  const setIsOpenOutline = setIsOpenOutlineProp || setIsOpenOutlineInternal;
  const editorContent = useSelector((state) => state.editor.editorInstancesDelta);
  const isReadOnly = useSelector((state) => state.editor.isReadOnly);
  const isUiLocked = useSelector((state) => state.editor.isUiLocked);
  // Cached preview + background load keeps the whole UI read-only; the summary
  // and AI actions must follow the same lock so stale content can't be mutated.
  const interactionsDisabled = isReadOnly || isUiLocked;
  const [headings, setHeadings] = useState([]);
  const summaryRef = useRef();
  const [aiModalVisibility, setAiModalVisibility] = useState(false);
  const [isOpenAi, setIsOpenAi] = useState(false);
  const documentVersionData = useSelector((state) => state.editor.documentVersionData);
  const summaryText = useSelector((state) => state.editor.editorSummary);
  const isChangeByUser = useSelector((state) => state.editor.isChangeByUser);
  const signatures = useSelector((state) => state.editor.signatures);
  const editor = useSelector((state) => state.editor.lexicalEditor);
  const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);

  useEffect(() => {
    if (documentVersionData.isVersionChange) {
      dispatch(setEditorSummary(documentVersionData.versionData?.summary || ''));
    }
  }, [documentVersionData]);

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorContent, 'text/html');
    const headingElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const extractedHeadings = Array.from(headingElements).map((element) => ({
      level: parseInt(element.tagName[1], 10),
      text: element.textContent.trim(),
      id: element.getAttribute('id'),
    }));
    setHeadings(extractedHeadings);
  }, [editorContent]);

  // Outline rows are the non-empty headings; the same filter is applied to the
  // live editor DOM so row index N maps to the Nth rendered heading.
  const outlineHeadings = useMemo(() => headings.filter((heading) => heading.text !== ''), [headings]);
  const [activeHeadingIndex, setActiveHeadingIndex] = useState(0);

  const getHeadingElements = useCallback(() => {
    const root = editor?.getRootElement?.();
    if (!root) return [];
    return Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6'))
      .filter((element) => element.textContent.trim() !== '');
  }, [editor]);

  const scrollToHeading = (index) => {
    const target = getHeadingElements()[index];
    if (!target) return;
    setActiveHeadingIndex(index);
    // Like Word's navigation pane: the caret lands at the end of the heading
    // so typing continues from there. Skipped when the document is read-only.
    if (editor?.isEditable?.()) {
      editor.update(() => {
        const node = $getNearestNodeFromDOMNode(target);
        if (node && typeof node.selectEnd === 'function') node.selectEnd();
      });
      // Lexical focuses with preventScroll, so the smooth scroll below owns the viewport.
      editor.focus();
    }
    // scrollIntoView (plus scroll-margin-top on the heading) stays correct under
    // the app's CSS zoom, unlike a hand-computed scrollTop.
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Scroll-spy: the last heading whose top has passed the canvas' top edge is
  // the one being read. Rects are compared in the same coordinate space, so
  // the app's zoom does not skew the result.
  useEffect(() => {
    const root = editor?.getRootElement?.();
    const panel = root?.closest('.editor-panel');
    if (!panel || outlineHeadings.length === 0) return undefined;

    let frame = 0;
    const update = () => {
      frame = 0;
      const panelTop = panel.getBoundingClientRect().top;
      const elements = getHeadingElements();
      let active = 0;
      elements.forEach((element, index) => {
        if (element.getBoundingClientRect().top - panelTop <= 48) active = index;
      });
      setActiveHeadingIndex(active);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    panel.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      panel.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [editor, outlineHeadings.length, getHeadingElements]);

  // Older builds pinned the panel width as an inline custom property on <html>;
  // clear it so the stylesheet's 240px always wins.
  useEffect(() => {
    document.documentElement.style.removeProperty('--outline-width');
    document.documentElement.classList.remove('outline-collapsed');
  }, []);

  useEffect(() => {
    if (isEditMode && summaryRef.current) {
      summaryRef.current.focus();
    }
  }, [isEditMode]);

  const debouncedSaveToDatabase = useRef(debounce((summary) => {
    if (isEffectivelyOffline()) {
      const currentEditorStateJSON = editor.getEditorState().toJSON();
      saveToLocalStorage(id, {
        milepadData: currentEditorStateJSON,
        summary: summary,
        Signatures: signatures,
      });
      return;
    }
    padServices.saveToDatabase(id, null, summary, dispatch);
    if (isChangeByUser) {
      emitLoadData({
        data: summary,
        type: 'summary',
        documentId: id,
      });
    }
  }, 800)).current;

  const handleTextareaChange = (e) => {
    dispatch(updateIsSaving({ isSaving: 'Saving...', lastSavedDateTime: '' }));
    debouncedSaveToDatabase(e.target.value);
    dispatch(setEditorSummary(e.target.value));
  };

  const toggleEdit = () => setIsEditMode((editing) => !editing);
  const finishEdit = () => setIsEditMode(false);

  const showAiText = (data) => {
    if (data) {
      dispatch(setEditorSummary(data));
      padServices.saveToDatabase(id, null, data, dispatch);
      if (isChangeByUser) {
        emitLoadData({
          data: data,
          type: 'summary',
          documentId: id,
        });
      }
    }
  };

  const hasSummary = !!(summaryText && summaryText.trim());

  const renderSummary = () => (
    <div className="dp-summary">
      <div className="dp-summary-header">
        <div className="dp-summary-title">Summary</div>
        <div className="dp-summary-actions">
          <button
            type="button"
            className="dp-btn dp-btn-28"
            title="Generate AI Summary"
            onClick={() => setAiModalVisibility(true)}
            disabled={interactionsDisabled}
          >
            <SparkleIcon />
          </button>
          <button
            type="button"
            className="dp-btn dp-btn-28"
            title={isEditMode ? 'Done editing' : 'Edit summary'}
            onClick={toggleEdit}
            disabled={interactionsDisabled}
          >
            <PencilIcon />
          </button>
        </div>
      </div>

      {(isEditMode || !hasSummary) ? (
        <div className={`dp-summary-edit${hasSummary ? '' : ' dp-summary-edit--empty'}`}>
          <textarea
            ref={summaryRef}
            value={summaryText || ''}
            onChange={handleTextareaChange}
            onBlur={finishEdit}
            placeholder="No summary yet. Tap the pencil to write one."
          />
        </div>
      ) : hasSummary ? (
        <div className="dp-summary-card">
          <div className="dp-summary-glow" />
          <div className="dp-summary-text">{summaryText}</div>
        </div>
      ) : null}
    </div>
  );

  const renderOutline = () => (
    <div className="dp-outline">
      <div className="dp-outline-header">
        <div className="dp-outline-label">OUTLINE</div>
        {outlineHeadings.length > 0 && (
          <div className="dp-outline-count">{outlineHeadings.length}</div>
        )}
      </div>
      <div className="dp-outline-list">
        {outlineHeadings.length > 0 ? (
          outlineHeadings.map((heading, index) => {
            const depth = Math.min(heading.level - 1, HEADING_SPECS.length - 1);
            const spec = HEADING_SPECS[depth];
            const isActive = index === activeHeadingIndex;
            return (
              <div
                key={index}
                role="button"
                tabIndex={0}
                title={heading.text}
                className={`dp-outline-row${isActive ? ' dp-outline-row--active' : ''}`}
                style={{ padding: depth === 0 ? '8px 10px' : `6px 10px 6px ${22 + (depth - 1) * 16}px` }}
                onClick={() => scrollToHeading(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    scrollToHeading(index);
                  }
                }}
              >
                {depth > 0 && (
                  <div className="dp-outline-rail" style={{ left: `${14 + (depth - 1.5) * 16}px` }} />
                )}
                {depth > 0 && (
                  <div
                    className="dp-outline-dash"
                    style={{ width: depth === 1 ? '7px' : '5px', background: depth === 1 ? '#a9b2cf' : '#cdd2e0' }}
                  />
                )}
                <div
                  className="dp-outline-chip"
                  style={isActive ? undefined : { background: spec.chipBg, color: spec.chipColor }}
                >
                  {spec.tag}
                </div>
                <div
                  className="dp-outline-text"
                  style={{ fontSize: spec.size, fontWeight: spec.weight, color: isActive ? undefined : spec.color }}
                >
                  {heading.text}
                </div>
              </div>
            );
          })
        ) : (
          <div className="dp-outline-empty">
            <OutlineEmptyIcon />
            <div className="dp-outline-empty-title">No headings yet</div>
            <div className="dp-outline-empty-hint">Use Heading 1–3 in the toolbar and they'll appear here.</div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isOpenOutline ? (
        <div className="dp-panel">
          <div className="dp-panel-header">
            <div className="dp-panel-title">Document panel</div>
            <button type="button" className="dp-btn dp-btn-26" title="Collapse panel" onClick={() => setIsOpenOutline(false)}>
              <PanelIcon direction="left" />
            </button>
          </div>
          {!isTemplateDocument && renderSummary()}
          {!isTemplateDocument && <div className="dp-divider" />}
          {renderOutline()}
        </div>
      ) : (
        <div className="dp-rail">
          <button type="button" className="dp-btn dp-btn-26" title="Expand panel" onClick={() => setIsOpenOutline(true)}>
            <PanelIcon direction="right" />
          </button>
        </div>
      )}
      <div>
        <AiTextButton
          summaryContent={editorContent}
          isOpen={isOpenAi}
          setIsOpenAi={setIsOpenAi}
          showAiText={showAiText}
          setAiModalVisibility={setAiModalVisibility}
          aiModalVisibility={aiModalVisibility}
        />
      </div>
    </>
  );
};

export default Headers;
